import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiFetch, setAuthToken } from '@/lib/api'
import { Eye, EyeOff, Lock, Mail, Shield } from 'lucide-react'
import Loginhero from '../assets/login-hero.png'
import WhiteLogo from '../assets/white-logo.png'
type AdminUser = {
  id: string
  email: string
  name?: string
  phone?: string
  role?: string
}

type AuthSuccessPayload = {
  user: AdminUser
  token: string
}

type AuthApiEnvelope = {
  success: true
  data: AuthSuccessPayload
}

function computeInitials(nameOrEmail: string) {
  const raw = nameOrEmail.trim()
  if (!raw) return 'A'
  return raw[0].toUpperCase()
}

export default function AdminLoginPage({
  onLogin,
}: {
  onLogin: (args: { token: string; user: { name: string; initials: string; role: string } }) => void
}) {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [keepSignedIn, setKeepSignedIn] = useState(true)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setIsSubmitting(true)
    try {
      const { data } = await apiFetch<AuthApiEnvelope>('/auth/admin-login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      })

      const user = data.user
      const token = data.token

      // Store token so refresh keeps you signed in.
      localStorage.setItem('adminAuthToken', token)
      setAuthToken(token)

      const name = typeof user.name === 'string' && user.name ? user.name : user.email
      onLogin({ token, user: { name, initials: computeInitials(name), role: user.role ?? 'admin' } })
      navigate('/')
    } catch (err) {
      const message =
        typeof err === 'string' ? err : err instanceof Error ? err.message : 'Login failed'
      console.error(message)
      setError(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-screen w-full flex-col bg-secondary overflow-x-hidden lg:h-screen lg:overflow-hidden lg:flex-row lg:items-stretch">
      {/* hero */}
      <div className="relative isolate flex min-h-screen w-full shrink-0 flex-col items-start bg-primary-dark  lg:top-0  lg:w-[45%]">
        {/* Clip only the background image, not the content */}
        <div className="pointer-events-none absolute inset-0 overflow-x-hidden top-0 left-0 right-0 bottom-0" aria-hidden>
          <img src={Loginhero} alt="" className="h-full w-full object-cover" />
        </div>
        <div
          className="pointer-events-none absolute inset-0 z-1 bg-[linear-gradient(180deg,rgba(17,13,8,0.92)_0%,rgba(17,13,8,0.78)_40%,rgba(17,13,8,0.95)_100%)]"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -left-20 top-[42.39%] bottom-[4.28%] z-2 w-[480px] shrink-0 rounded-[240px] bg-[radial-gradient(70.71%_70.71%_at_50%_50%,rgba(75,44,77,0.22)_0%,rgba(75,44,77,0)_70%)]"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute bottom-[-80px] right-[-232.28px] z-3 h-[420px] w-[420px] shrink-0 rounded-[210px] bg-[radial-gradient(70.71%_70.71%_at_50%_50%,rgba(201,168,76,0.14)_0%,rgba(201,168,76,0)_70%)]"
          aria-hidden
        />
        <div className="relative z-10 flex h-full w-full flex-col px-6 py-10 sm:px-10 sm:py-12 lg:px-14 lg:py-12">
          {/* logo row */}
          <div className="flex w-full items-center justify-between">
            <img src={WhiteLogo} alt="Rohan Gurram" className="h-auto w-auto" />
            <span className="inline-flex items-center gap-2 rounded-full border border-accent-lilac/28 bg-primary/22 px-3.5 py-1.5 font-poppins text-[9px] font-bold uppercase tracking-[0.22em] text-accent-lilac">
              <span
                className="h-1.5 w-1.5 rounded-full bg-accent-lilac "
                aria-hidden
              />
              <span>Admin Console</span>
            </span>
          </div>

          {/* center content */}
          <div className="flex flex-1 flex-col justify-center">
            <div className="mt-10 w-full">
              <div className="flex items-center gap-4">
                <span className="h-px w-7 bg-accent-gold" />
                <p className="font-poppins text-2xs font-bold leading-4 uppercase tracking-[0.34em] text-accent-gold">
                  The Network · Behind The Curtain
                </p>
              </div>

            <h1 className="mt-6 font-tt-ramillas-trl text-[46px] font-bold leading-[1.08] tracking-[-0.02em] text-white sm:text-[56px] lg:text-[64px] lg:leading-[70.6px]">
                Run the <br />
                <span className="font-medium italic text-accent-lilac">world</span> that
                <br />
                runs the brand.
              </h1>

              <p className="mt-6 font-poppins font-normal text-[14px] leading-[1.7] text-white/50">
                Every episode, every drop, every gathering, every member
                <br />— managed from one controlled console. Sign in to take the
                <br />
                room.
              </p>

              <div className="mt-10 max-w-lg rounded-2xl border border-white/8 bg-white/6 px-[22px] py-[18px]">
                <div className="grid grid-cols-4 gap-4">
                  {[
                    {
                      value: "847",
                      label: (
                        <>
                          Active
                          <br />
                          Members
                        </>
                      ),
                      color: "accent-gold",
                    },
                    {
                      value: "12",
                      label: (
                        <>
                          Events This
                          <br />
                          Year
                        </>
                      ),
                      color: "white",
                    },
                    { value: "07", label: "Chapters Live", color: "accent-gold" },
                    {
                      value: "03",
                      label: (
                        <>
                          Drops
                          <br />
                          Upcoming
                        </>
                      ),
                      color: "white",
                    },
                  ].map((s, idx) => (
                    <div key={`${s.value}-${idx}`} className="flex items-center gap-4">
                      <div className="flex flex-col">
                        <p
                          className="font-poppins text-[22px] font-bold leading-none"
                          style={{ color: s.color }}
                        >
                          {s.value}
                        </p>
                        <p className="mt-2 font-poppins text-[9px] font-semibold uppercase leading-[1.35] tracking-[0.28em] text-white/40">
                          {s.label}
                        </p>
                      </div>
                      {idx !== 3 ? (
                        <span className="ml-auto h-10 w-px bg-white/6" aria-hidden />
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* footer */}
          <div className="mt-10 flex items-center justify-between">
            <p className="font-poppins font-medium text-2xs uppercase tracking-[0.22em] text-white/28">
              © 2025 Rohan Gurram
            </p>
            <p className="text-right font-tt-ramillas-trl text-sm italic leading-normal text-white/40">
              “Built in the dark.
              <br />
              Worn in the light.”
            </p>
          </div>
        </div>
      </div>

      {/* form */}
      <div className="flex w-full flex-1 flex-col items-center justify-start px-6 py-10 lg:w-[55%] lg:h-screen lg:overflow-y-auto lg:py-12">
        <div className="w-full max-w-[440px]">
          <p className="font-poppins text-2xs font-bold uppercase tracking-[0.34em] text-primary">
            Admin login
          </p>
          <h2 className="mt-4 font-tt-ramillas-trl text-3xl md:text-4xl xl:text-5xl font-bold leading-[54px] tracking-[-0.01em] text-text-dark">
            Welcome <span className="font-medium italic text-primary">back</span>
          </h2>
          <p className="mt-3 font-poppins font-normal text-sm leading-[22.1px] text-text-secondary">
            Sign in with your admin credentials. Two-factor authentication is required for every session.
          </p>

          {error ? (
            <div
              role="alert"
              className="mt-6 rounded-lg border border-red-900 bg-red-900/10 px-3 py-2 text-xs text-red-900 font-medium"
            >
              {error}
            </div>
          ) : null}

          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <label
                htmlFor="admin-email"
                className="block font-poppins text-2xs leading-4 font-bold uppercase tracking-[1.6px] text-text-dark"
              >
                Email address
              </label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted " />
                <input
                  id="admin-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-11 sm:h-12 w-full rounded-lg border border-border-dark/9 bg-white px-11 text-sm text-text-dark transition placeholder:text-placeholder-muted outline-none! focus:outline-none! focus-visible:outline-none! focus:ring-0! focus-visible:ring-0! focus:shadow-none! focus-visible:shadow-none!"
                  placeholder="admin@rohangurram.com"
                  autoComplete="email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label
                htmlFor="admin-password"
                className="block font-poppins text-2xs leading-4 font-bold uppercase tracking-[1.6px] text-text-dark"
              >
                Password
              </label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
                <input
                  id="admin-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11 sm:h-12 w-full rounded-lg border border-border-dark/9 bg-white px-11 pr-12 text-sm text-text-dark transition placeholder:text-placeholder-muted outline-none! focus:outline-none! focus-visible:outline-none! focus:ring-0! focus-visible:ring-0! focus:shadow-none! focus-visible:shadow-none!"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-md text-ink-muted"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between gap-4">
              <label className="inline-flex items-center gap-2 font-poppins text-xs-plus text-text-secondary">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded-sm border-primary accent-primary"
                  checked={keepSignedIn}
                  onChange={(e) => setKeepSignedIn(e.target.checked)}
                />
                Keep me signed in for 14 days
              </label>
              <button
                type="button"
                className="font-poppins text-xs leading-5 font-medium text-primary hover:text-primary/80"
              >
                Forgot password?
              </button>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="mt-1 inline-flex h-12 w-full items-center justify-center rounded-md bg-primary font-poppins text-xs-plus font-extrabold uppercase tracking-[0.32em] text-white transition hover:bg-primary/92 disabled:pointer-events-none disabled:opacity-60 cursor-pointer"
            >
              {isSubmitting ? 'Logging in…' : 'Login'}
            </button>

            <div className="rounded-[10px] border border-border-dark/9 bg-warm-cream px-4 py-3.5">
              <div className="flex gap-3">
                <span className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-lg border border-border-dark/9 bg-secondary">
                  <Shield className="h-4 w-4 text-primary" aria-hidden />
                </span>
                <p className="font-poppins text-[12px] leading-[18.4px] text-text-secondary">
                  <span className="font-semibold text-text-dark">Encrypted session.</span>{" "}
                  Your credentials are protected by TLS <br className='hidden sm:block'/> 1.3. This console is monitored — every admin action  is <br className='hidden sm:block'/>logged and auditable.
                </p>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
